import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    Legend,
    ReferenceArea,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import type { ComponentProps } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { m } from '@/lib/paraglide/messages'

interface StockMovementChartProps {
    data: { period: string; stock_in: number; stock_out: number }[]
}

const STOCK_IN_COLOR = 'hsl(142, 76%, 36%)'
const STOCK_OUT_COLOR = 'hsl(25, 95%, 53%)'

type BarLabelValue = Parameters<
    NonNullable<ComponentProps<typeof LabelList>['formatter']>
>[0]

function formatBarLabel(value: BarLabelValue): string {
    if (value === undefined || value === null) return ''
    const n = typeof value === 'string' ? Number(value) : value
    if (!Number.isFinite(n) || n === 0) return ''
    return n.toLocaleString('vi-VN')
}

interface ChartTooltipProps {
    active?: boolean
    payload?: Array<{
        value: number
        name?: string
        dataKey?: string
    }>
    label?: string
}

function StockMovementTooltip({ active, payload, label }: ChartTooltipProps) {
    if (!active || !payload?.length) return null
    return (
        <div className="rounded-md border bg-background px-3 py-2 shadow-sm text-sm">
            <p className="font-medium mb-1">{label}</p>
            {payload.map((entry) => (
                <p
                    key={entry.dataKey}
                    className={
                        entry.dataKey === 'stock_in'
                            ? 'text-green-600'
                            : 'text-orange-500'
                    }
                >
                    {entry.dataKey === 'stock_in'
                        ? m.report_chart_stock_in()
                        : m.report_chart_stock_out()}
                    : {entry.value}
                </p>
            ))}
        </div>
    )
}

export function StockMovementChart({ data }: StockMovementChartProps) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>{m.dashboard_warehouse_movement_title()}</CardTitle>
            </CardHeader>
            <CardContent>
                {data.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                        {m.common_no_data()}
                    </p>
                ) : (
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart
                            data={data}
                            margin={{ top: 24, right: 12, bottom: 5, left: 0 }}
                            barCategoryGap="25%"
                            barGap={4}
                        >
                            {/* Alternating background bands per day make each
                                day's bars visually grouped as a "column" even
                                when the in/out magnitudes differ. */}
                            {data.map((d, idx) =>
                                idx % 2 === 0 ? (
                                    <ReferenceArea
                                        key={d.period}
                                        x1={d.period}
                                        x2={d.period}
                                        yAxisId="left"
                                        fill="currentColor"
                                        fillOpacity={0.04}
                                        ifOverflow="extendDomain"
                                    />
                                ) : null,
                            )}
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                className="stroke-muted"
                            />
                            <XAxis
                                dataKey="period"
                                tick={{ fontSize: 12 }}
                                tickLine={{ stroke: 'currentColor' }}
                                axisLine={{ stroke: 'currentColor' }}
                                interval={0}
                                className="text-muted-foreground"
                            />
                            {/* Dual Y axis: left scales to stock_in, right scales to
                                stock_out independently. Stops the larger series from
                                flattening the smaller one when magnitudes differ. */}
                            <YAxis
                                yAxisId="left"
                                tick={{ fontSize: 12, fill: STOCK_IN_COLOR }}
                                className="text-muted-foreground"
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fontSize: 12, fill: STOCK_OUT_COLOR }}
                                className="text-muted-foreground"
                            />
                            <Tooltip content={<StockMovementTooltip />} />
                            <Legend
                                formatter={(value) =>
                                    value === 'stock_in'
                                        ? m.report_chart_stock_in()
                                        : m.report_chart_stock_out()
                                }
                            />
                            <Bar
                                yAxisId="left"
                                dataKey="stock_in"
                                fill={STOCK_IN_COLOR}
                                radius={[4, 4, 0, 0]}
                            >
                                <LabelList
                                    dataKey="stock_in"
                                    position="top"
                                    fontSize={11}
                                    fill={STOCK_IN_COLOR}
                                    formatter={formatBarLabel}
                                />
                            </Bar>
                            <Bar
                                yAxisId="right"
                                dataKey="stock_out"
                                fill={STOCK_OUT_COLOR}
                                radius={[4, 4, 0, 0]}
                            >
                                <LabelList
                                    dataKey="stock_out"
                                    position="top"
                                    fontSize={11}
                                    fill={STOCK_OUT_COLOR}
                                    formatter={formatBarLabel}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    )
}
